import { useState, useEffect, useRef } from 'react'

import { decodeSvgBuffer } from '../../../shared/utils/svgUtils.js'
import { Logger }          from '../../../shared/utils/logger.js'
import { FETCH_STATUS }    from '../../../shared/constants/fetchStatus.js'
import { HERO_VIEW }       from '../../../shared/constants/heroView.js'

/**
 * Normalizes outline SVG root sizing so it overlays frame images consistently
 * inside ModelView after responsive scaling/max-height constraints.
 *
 * Frame images use CSS `object-fit: cover`; for inline SVG the equivalent is
 * `preserveAspectRatio="xMidYMid slice"` with 100% root dimensions.
 *
 * @param {string} svgString
 * @returns {string}
 */
export function normalizeOutlineSvgLayout(svgString) {
  if (!svgString) return svgString
  if (typeof DOMParser === 'undefined' || typeof XMLSerializer === 'undefined') return svgString

  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, 'image/svg+xml')
  const svgEl = doc.querySelector('svg')

  if (!svgEl) return svgString

  svgEl.setAttribute('preserveAspectRatio', 'xMidYMid slice')
  svgEl.setAttribute('width', '100%')
  svgEl.setAttribute('height', '100%')

  return new XMLSerializer().serializeToString(svgEl)
}

// ── URL builder ────────────────────────────────────────────────────────────────

/**
 * Builds the absolute URL for an outline SVG file.
 *
 * Convention (mirrors generator output structure):
 * - VISUALIZATION  → `{dataUrl}site/vis/outlines_{outline}.svg`
 * - SITE           → `{dataUrl}site/render_360/outlines_{outline}.svg`
 * - BUILDING       → `{dataUrl}buildings/{idCad}/render_360/outlines_{outline}.svg`
 *
 * @param {string}      dataUrl
 * @param {string}      activeView     — HERO_VIEW value
 * @param {string}      activeOutline  — HERO_OUTLINE value ('buildings'|'tiers'|'apartments')
 * @param {string|null} activeBuildingIdCad
 * @returns {string|null}  null when required params are missing (e.g. BUILDING without idCad)
 */
export function buildOutlineSvgUrl(dataUrl, activeView, activeOutline, activeBuildingIdCad) {
  if (!dataUrl || !activeView || !activeOutline) return null

  const base    = dataUrl.endsWith('/') ? dataUrl : dataUrl + '/'
  const file    = `outlines_${activeOutline}.svg`

  switch (activeView) {
    case HERO_VIEW.VISUALIZATION:
      // return `${base}site/vis/${file}`  // OLD VERSION WITH vis/ in site/ directory
      return `${base}website/vis/${file}`

    case HERO_VIEW.SITE:
      return `${base}site/render_360/${file}`

    case HERO_VIEW.BUILDING:
      if (!activeBuildingIdCad) return null
      return `${base}buildings/${activeBuildingIdCad}/render_360/${file}`

    default:
      return null
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * useOutlineSvgFetch
 *
 * Fetches a single SVG outline file for the current view + outline combination.
 * Results are cached in a `useRef` map keyed by URL — re-mounting or switching
 * back to a previously loaded combination skips the network request.
 *
 * Reacts to changes in `activeView`, `activeOutline`, and `activeBuildingIdCad`.
 *
 * @param {object}      params
 * @param {string}      params.dataUrl             — project data base URL
 * @param {string}      params.activeView           — HERO_VIEW value
 * @param {string}      params.activeOutline        — HERO_OUTLINE value
 * @param {string|null} params.activeBuildingIdCad  — required when activeView === BUILDING
 *
 * @returns {{ svgString: string|null, svgStatus: string }}
 *   `svgString` — raw SVG markup ready for `dangerouslySetInnerHTML`, or null
 *   `svgStatus` — one of FETCH_STATUS.LOADING | READY | ERROR
 */
export function useOutlineSvgFetch({ dataUrl, activeView, activeOutline, activeBuildingIdCad }) {
  const cache = useRef({})

  const [svgString, setSvgString] = useState(null)
  const [svgStatus, setSvgStatus] = useState(FETCH_STATUS.LOADING)

  useEffect(() => {
    const url = buildOutlineSvgUrl(dataUrl, activeView, activeOutline, activeBuildingIdCad)

    if (!url) {
      setSvgString(null)
      setSvgStatus(FETCH_STATUS.LOADING)
      return
    }

    // Serve from cache when available
    if (url in cache.current) {
      setSvgString(cache.current[url])
      setSvgStatus(FETCH_STATUS.READY)
      return
    }

    let cancelled = false
    setSvgStatus(FETCH_STATUS.LOADING)

    async function fetchSvg() {
      try {
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${url}`)
        }

        const contentType = response.headers.get('content-type') ?? ''
        if (contentType.includes('text/html')) {
          throw new Error(`Expected SVG but got HTML — file may not exist: ${url}`)
        }

        const buffer      = await response.arrayBuffer()
        const rawSvgText  = decodeSvgBuffer(buffer)
        const svgText     = normalizeOutlineSvgLayout(rawSvgText)

        cache.current[url] = svgText

        if (!cancelled) {
          setSvgString(svgText)
          setSvgStatus(FETCH_STATUS.READY)
        }
      } catch (err) {
        if (!cancelled) {
          Logger.error('[useOutlineSvgFetch] Failed to fetch SVG:', err)
          setSvgString(null)
          setSvgStatus(FETCH_STATUS.ERROR)
        }
      }
    }

    fetchSvg()
    return () => { cancelled = true }

  }, [dataUrl, activeView, activeOutline, activeBuildingIdCad])

  return { svgString, svgStatus }
}
